import { useCallback, useEffect, useRef, useState } from "react"
import { useConfig } from "@/config/context"
import { renderGoogleButton, type NostrConnectOffer, type SignerMethod } from "@/nostr/signers"
import { useAuth } from "@/state/AuthContext"
import { useToast } from "@/state/ToastContext"
import { randomHex } from "@/utils/hex"
import { CreateAccountDialog } from "./CreateAccountDialog"
import { Icon } from "./Icon"
import { Modal } from "./Modal"

export interface LoginDialogProps {
  open: boolean
  onClose: () => void
}

type View = "options" | "nip46" | "google" | "nsec" | "npub"

const VIEW_TITLES: Record<Exclude<View, "options">, string> = {
  nip46: "Remote signer",
  google: "Continue with Google",
  nsec: "Private key",
  npub: "Read-only session",
}

const METHOD_ICONS: Record<SignerMethod, string> = {
  nip07: "puzzle",
  nip46: "server",
  google: "globe",
  nsec: "key",
  npub: "eye",
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * Sign-in dialog: browser extension, NIP-46 remote signer, Google or a private
 * key, plus the entry point for creating a brand new account.
 */
export function LoginDialog({ open, onClose }: LoginDialogProps) {
  const config = useConfig()
  const { push } = useToast()
  const {
    hasExtension,
    canUseGoogle,
    allowSignup,
    loginWithExtension,
    loginWithNsec,
    loginWithNpub,
    loginWithBunker,
    createRemoteSignerOffer,
    pairWithRemoteSigner,
    loginWithGoogle,
    accounts,
    switchAccount,
  } = useAuth()

  const [view, setView] = useState<View>("options")
  const [creating, setCreating] = useState(false)
  const [nsec, setNsec] = useState("")
  const [npub, setNpub] = useState("")
  const [bunkerInput, setBunkerInput] = useState("")
  const [pairUri, setPairUri] = useState<string>()
  const [authUrl, setAuthUrl] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const offerRef = useRef<NostrConnectOffer | undefined>(undefined)
  const pairAttempt = useRef(0)
  const googleSlot = useRef<HTMLDivElement>(null)
  const errorRef = useRef<HTMLParagraphElement>(null)

  const reset = useCallback(() => {
    setView("options")
    setCreating(false)
    setNsec("")
    setNpub("")
    setBunkerInput("")
    setPairUri(undefined)
    setAuthUrl(undefined)
    setBusy(false)
    setError(undefined)
    offerRef.current = undefined
    pairAttempt.current += 1
  }, [])

  useEffect(() => {
    if (!open) reset()
  }, [open, reset])

  const run = useCallback(
    async (action: () => Promise<void> | void) => {
      setBusy(true)
      setError(undefined)
      try {
        await action()
        onClose()
      } catch (loginError) {
        setError(describe(loginError))
        requestAnimationFrame(() => errorRef.current?.focus())
      } finally {
        setBusy(false)
      }
    },
    [onClose],
  )

  // Google requires its own button, so it is rendered when the panel opens.
  useEffect(() => {
    if (!open || creating || view !== "google" || !canUseGoogle) return
    const slot = googleSlot.current
    if (!slot) return
    let dispose: (() => void) | undefined
    let cancelled = false
    const nonce = randomHex(16)
    renderGoogleButton(slot, {
      clientId: config.auth.googleClientId,
      nonce,
      onCredential: (idToken) => void run(() => loginWithGoogle(idToken, nonce)),
    })
      .then((cleanup) => {
        if (cancelled) cleanup()
        else dispose = cleanup
      })
      .catch((googleError: unknown) => {
        if (!cancelled) setError(describe(googleError))
      })
    return () => {
      cancelled = true
      dispose?.()
    }
  }, [open, creating, view, canUseGoogle, config.auth.googleClientId, loginWithGoogle, run])

  const startPairing = () => {
    let offer: NostrConnectOffer
    try {
      offer = createRemoteSignerOffer()
    } catch (offerError) {
      setError(describe(offerError))
      return
    }
    offerRef.current = offer
    setPairUri(offer.uri)
    setError(undefined)
    setBusy(true)
    const attempt = (pairAttempt.current += 1)
    void pairWithRemoteSigner(offer, { onAuthUrl: setAuthUrl })
      .then(() => {
        if (pairAttempt.current !== attempt) return
        setBusy(false)
        onClose()
      })
      .catch((pairError: unknown) => {
        if (pairAttempt.current !== attempt) return
        setBusy(false)
        setPairUri(undefined)
        offerRef.current = undefined
        setError(describe(pairError))
        requestAnimationFrame(() => errorRef.current?.focus())
      })
  }

  const cancelPairing = () => {
    pairAttempt.current += 1
    offerRef.current = undefined
    setPairUri(undefined)
    setBusy(false)
  }

  const copyPairingUri = async () => {
    if (!pairUri) return
    try {
      await navigator.clipboard.writeText(pairUri)
      push("success", "Pairing link copied")
    } catch {
      push("error", "Could not copy the pairing link")
    }
  }

  const backToOptions = () => {
    if (busy) return
    cancelPairing()
    setView("options")
    setError(undefined)
    setAuthUrl(undefined)
    setBunkerInput("")
  }

  if (creating) {
    return (
      <CreateAccountDialog
        open={open}
        onClose={onClose}
        onBack={() => {
          setCreating(false)
          setView("options")
        }}
      />
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={view === "options" ? "Add an account" : VIEW_TITLES[view]}
      description={view === "options" ? "Choose how you want to sign events." : undefined}
      size="medium"
      className="login-dialog"
      headerAction={
        view !== "options" ? (
          <button type="button" className="login-back" onClick={backToOptions} disabled={busy}>
            <Icon name="chevron-left" size={14} /> All options
          </button>
        ) : undefined
      }
    >
      {view === "options" && (
        <div className="login-options">
          <button
            type="button"
            className="login-primary"
            disabled={busy}
            onClick={() => void run(loginWithExtension)}
          >
            <Icon name="puzzle" size={18} />
            <span>{busy ? "Waiting for your extension…" : "Login with Browser Extension"}</span>
            <Icon name="chevron-right" size={16} />
          </button>
          <p className="login-hint">
            {hasExtension
              ? "Alby, nos2x and other NIP-07 signers sign events without exposing your key."
              : "No NIP-07 extension was detected in this browser."}
          </p>

          <div className="login-divider" role="separator">
            OR
          </div>

          <div className="login-methods">
            <button type="button" className="login-method" onClick={() => setView("nip46")}>
              <span className="login-method-icon">
                <Icon name="server" size={22} />
              </span>
              <span>Remote signer</span>
            </button>
            <button type="button" className="login-method" onClick={() => setView("google")}>
              <span className="login-method-icon google" aria-hidden="true">
                G
              </span>
              <span>Google</span>
            </button>
            <button type="button" className="login-method" onClick={() => setView("nsec")}>
              <span className="login-method-icon">
                <Icon name="key" size={22} />
              </span>
              <span>Private Key</span>
            </button>
          </div>

          {allowSignup && (
            <p className="login-signup">
              Don&apos;t have an account yet?{" "}
              <button type="button" onClick={() => setCreating(true)}>
                Create New Account
                <Icon name="chevron-right" size={14} />
              </button>
            </p>
          )}
        </div>
      )}

      {view === "nip46" && (
        <form
          className="login-panel"
          onSubmit={(event) => {
            event.preventDefault()
            void run(() => loginWithBunker(bunkerInput, { onAuthUrl: setAuthUrl }))
          }}
        >
          <label htmlFor="bunker-input">Bunker connection</label>
          <input
            id="bunker-input"
            value={bunkerInput}
            onChange={(event) => setBunkerInput(event.target.value)}
            placeholder="bunker://… or signer@domain.com"
            spellCheck={false}
            autoComplete="off"
          />
          <p className="muted small">
            Copy the connection string from your signer (Amber, nsec.app, a nsecBunker…). Requests are signed
            on the signer; your key never reaches this browser.
          </p>
          <button type="submit" className="btn btn-primary" disabled={busy || bunkerInput.trim().length < 6}>
            {busy && !pairUri ? "Waiting for the signer…" : "Connect remote signer"}
          </button>

          <div className="login-divider" role="separator">
            OR
          </div>

          <div className="login-pairing">
            <p className="muted small">Pair with a signer app instead:</p>
            {pairUri ? (
              <>
                <input
                  className="mono"
                  readOnly
                  value={pairUri}
                  aria-label="Pairing link"
                  onFocus={(event) => event.currentTarget.select()}
                />
                <div className="wizard-actions-row">
                  <button type="button" className="btn btn-ghost" onClick={() => void copyPairingUri()}>
                    <Icon name="copy" size={16} /> Copy pairing link
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={cancelPairing}>
                    Cancel
                  </button>
                </div>
                <p className="muted small">
                  {busy
                    ? "Waiting for your signer to approve the session…"
                    : "Open the link in your signer app."}
                </p>
              </>
            ) : (
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={startPairing}>
                <Icon name="link" size={16} /> Create pairing link
              </button>
            )}
          </div>
        </form>
      )}

      {view === "google" && (
        <div className="login-panel">
          {canUseGoogle ? (
            <>
              <p className="muted">
                Sign in with your Google account to unlock the identity attached to it. {config.branding.name}{" "}
                never receives your Google password.
              </p>
              <div className="google-slot" ref={googleSlot} />
              {busy && <p className="muted small">Verifying your Google account…</p>}
              <p className="muted small">
                The key for this identity is derived from your Google account, so signing needs a fresh Google
                sign-in after a page reload.
              </p>
            </>
          ) : (
            <div className="notice">
              Google sign-in is not enabled for this deployment. An operator can turn it on by setting{" "}
              <code>auth.googleClientId</code> (or <code>VITE_APP_GOOGLE_CLIENT_ID</code>). Meanwhile use a
              browser extension, a remote signer or a private key.
            </div>
          )}
        </div>
      )}

      {view === "nsec" && (
        <form
          className="login-panel"
          onSubmit={(event) => {
            event.preventDefault()
            void run(() => loginWithNsec(nsec))
          }}
        >
          <label htmlFor="nsec-input">Private key (nsec1…)</label>
          <input
            id="nsec-input"
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={nsec}
            onChange={(event) => setNsec(event.target.value)}
            placeholder="nsec1…"
          />
          <p className="muted small">
            The key stays in memory for this tab only. It is never written to storage — reloading the page
            ends the signing session.
          </p>
          <button type="submit" className="btn btn-primary" disabled={busy || nsec.length < 10}>
            Sign in
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setView("npub")}>
            <Icon name="eye" size={16} /> Browse read-only instead
          </button>
        </form>
      )}

      {view === "npub" && (
        <form
          className="login-panel"
          onSubmit={(event) => {
            event.preventDefault()
            void run(() => loginWithNpub(npub))
          }}
        >
          <label htmlFor="npub-input">Public key (npub1… or nprofile1…)</label>
          <input
            id="npub-input"
            value={npub}
            onChange={(event) => setNpub(event.target.value)}
            placeholder="npub1…"
            spellCheck={false}
          />
          <p className="muted small">Read-only mode: you can browse everything but cannot publish.</p>
          <button type="submit" className="btn btn-primary" disabled={busy || npub.length < 10}>
            Browse read-only
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setView("nsec")}>
            <Icon name="key" size={16} /> Use a private key instead
          </button>
        </form>
      )}

      {authUrl && (
        <p className="notice login-auth-url">
          Your signer asks for approval:{" "}
          <a href={authUrl} target="_blank" rel="noreferrer noopener">
            open the signer page
          </a>
          .
        </p>
      )}

      {error && (
        <p className="form-error" role="alert" tabIndex={-1} ref={errorRef}>
          {error}
        </p>
      )}

      {view === "options" && accounts.length > 0 && (
        <div className="login-accounts">
          <h3>Saved accounts</h3>
          {accounts.map((account) => (
            <button
              key={account.pubkey}
              type="button"
              className="account-row"
              onClick={() => void run(() => switchAccount(account.pubkey))}
            >
              <Icon name={METHOD_ICONS[account.method]} size={14} />
              <span>{account.pubkey.slice(0, 16)}…</span>
              <span className="badge">{account.method === "npub" ? "read-only" : account.method}</span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  )
}
